import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CalendarCheckIcon, CalendarClockIcon, CalendarPlusIcon, HistoryIcon, HourglassIcon, PlaneIcon, SunIcon } from 'lucide-react';
import { timeOffEntries } from '../../data/timeOff';
import type { Employee, Navigate, PtoRequest } from '../../types/hr';
import { TODAY, formatDate, formatDays, formatRange } from '../../utils/format';
import { milestoneDate, nextTier, tierForYears, tierPosting, tierRate } from '../../utils/policy';
import { accrualStart, balanceFor, getPolicy, historyFor, scheduledDays, serviceYears, usedYtd } from '../../utils/timeOff';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
import { FilterSelect } from '../ui/FilterSelect';
import { TbdBadge } from '../ui/TbdBadge';
interface TimeOffTabProps {
  employee: Employee;
  requests: PtoRequest[];
  onRequest: () => void;
  onNavigate: Navigate;
}
export function TimeOffTab({
  employee,
  requests,
  onRequest,
  onNavigate
}: TimeOffTabProps) {
  const policy = getPolicy(employee.policyId);
  const balance = balanceFor(employee.id);
  const history = useMemo(() => historyFor(employee), [employee]);
  const years = Array.from(new Set(history.map(h => String(h.year))));
  const [year, setYear] = useState(years[0] ?? '2026');
  const [policyFilter, setPolicyFilter] = useState(policy?.name ?? 'All');
  const used = usedYtd(employee);
  const scheduled = scheduledDays(employee.id);
  const isNewHire = employee.policyId === 'newhire';
  const isRegular = employee.policyId === 'regular';
  const accrualDate = accrualStart(employee.hireDate);
  const schedule = policy?.form.schedule ?? [];
  const service = serviceYears(employee);
  const currentTier = schedule.length ? tierForYears(schedule, service) : undefined;
  const upcomingTier = schedule.length ? nextTier(schedule, service) : undefined;
  const todayIso = format(TODAY, 'yyyy-MM-dd');
  const upcoming = [...timeOffEntries.filter(e => e.employeeId === employee.id && e.end >= todayIso).map(e => ({
    id: e.id,
    start: e.start,
    end: e.end,
    days: e.days,
    label: e.policyLabel,
    pending: false
  })), ...requests.filter(r => r.employeeId === employee.id && r.status !== 'denied').map(r => ({
    id: r.id,
    start: r.start,
    end: r.end,
    days: r.days,
    label: getPolicy(r.policyId)?.name ?? '',
    pending: r.status === 'pending'
  }))].sort((a, b) => a.start.localeCompare(b.start));
  const rows = history.filter(h => String(h.year) === year);
  return <div className="space-y-6" data-id="element-453" data-mp="2nb970w8d6kym">
      {policy && balance ? <div className="grid grid-cols-1 gap-6 xl:grid-cols-3" data-id="element-454" data-mp="2sam0w0xnksap">
          <div className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-soft ring-1 ring-slate-900/[0.04] xl:col-span-2" data-id="element-455" data-mp="2s9srstb81qpe">
            <div className="absolute right-0 top-0 h-full w-1.5 bg-flame" aria-hidden="true" data-id="element-456" data-mp="31ba5y652jibg" />
            <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-457" data-mp="pnx6nxdgbsqm">
              <div data-id="element-458" data-mp="plu64jeoah7n">
                <div className="flex items-center gap-2" data-id="element-459" data-mp="kou20e1dvoj6">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-azure/10 text-azure" data-id="element-460" data-mp="knakyl7wl9gc">
                    <SunIcon className="h-4 w-4" data-id="element-461" data-mp="gnigokpjlpgs" />
                  </span>
                  <button type="button" onClick={() => onNavigate({
                page: 'policy',
                policyId: policy.id
              })} className="text-sm font-semibold text-navy hover:text-azure" data-id="element-462" data-mp="bpgzy6fla28h">
                    {policy.name}
                  </button>
                  {policy.hasTbd && <TbdBadge data-id="element-463" data-mp="zm4k468j7snv" />}
                </div>
                <p className="mt-5 flex items-baseline gap-2" data-id="element-464" data-mp="zktgc6f36kwr">
                  <span className="text-5xl font-semibold tracking-tight text-navy" data-id="element-465" data-mp="unjji5pdb6yw">{balance.available}</span>
                  <span className="text-lg font-medium text-slate-500" data-id="element-466" data-mp="1s2nno0wpia67">days</span>
                </p>
                <p className="mt-1 text-sm text-muted" data-id="element-467" data-mp="1nbfcazbtgeqe">Available balance</p>
              </div>
              <Button variant="primary" icon={CalendarPlusIcon} onClick={onRequest} data-id="element-468" data-mp="16vws8ndzzfm2">
                Request time off
              </Button>
            </div>
            <dl className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-100 pt-5" data-id="element-469" data-mp="1bswmm11hitxc">
              <div data-id="element-470" data-mp="xxkt1wz2f9i0">
                <dt className="text-xs text-muted" data-id="element-471" data-mp="12ul0ab7niiap">Used YTD</dt>
                <dd className="mt-1 text-lg font-semibold text-navy" data-id="element-472" data-mp="12v4fvl9twulk">{formatDays(used)}</dd>
              </div>
              <div data-id="element-473" data-mp="1qkyqrxal8936">
                <dt className="text-xs text-muted" data-id="element-474" data-mp="1ulr3x1mkyy4q">Scheduled</dt>
                <dd className="mt-1 text-lg font-semibold text-navy" data-id="element-475" data-mp="1ul911m5exlnu">{formatDays(scheduled)}</dd>
              </div>
              <div data-id="element-476" data-mp="1gs9j7di5rfd4">
                <dt className="text-xs text-muted" data-id="element-477" data-mp="1ltgs5fw1ekld">After scheduled</dt>
                <dd className="mt-1 text-lg font-semibold text-navy" data-id="element-478" data-mp="1lrm3bqhna8c0">{formatDays(balance.available - scheduled)}</dd>
              </div>
            </dl>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-1" data-id="element-479" data-mp="2eat38dqzvvmw">
            <div className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-480" data-mp="2nnrwk3pjvncs">
              <div className="flex items-center gap-2 text-xs font-medium text-muted" data-id="element-481" data-mp="2jnhomli76mva">
                {isNewHire ? <HourglassIcon className="h-4 w-4 text-flame" data-id="element-482" data-mp="2xemjlaameqza" /> : <CalendarClockIcon className="h-4 w-4 text-azure" data-id="element-483" data-mp="2xgh5orxmop4y" />}
                {isNewHire ? 'Waiting period' : 'Accrual'}
              </div>
              {isNewHire ? <p className="mt-2 text-lg font-semibold text-navy" data-id="element-484" data-mp="24q7izss8kb7h">Begins {format(accrualDate, 'MMM d')}</p> : currentTier ? <p className="mt-2 text-lg font-semibold text-navy" data-id="element-485" data-mp="2envg2pdbdust">{tierRate(currentTier)}</p> : <div className="mt-2 flex items-center gap-2" data-id="element-486" data-mp="29o91abhu86hw">
                  <span className="text-lg font-semibold text-navy" data-id="element-487" data-mp="29qva0eggss1p">Rate</span>
                  <TbdBadge data-id="element-488" data-mp="3q1h2w3vpbkb6" />
                </div>}
              <p className="mt-0.5 text-xs text-muted" data-id="element-489" data-mp="3l4qnlirq0sqt">
                {isNewHire ? '1 day / month after 30 days, prorated' : currentTier ? `${service} ${service === 1 ? 'year' : 'years'} of service · posted ${tierPosting(currentTier)}` : 'No accrual schedule set'}
              </p>
            </div>
            <div className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-490" data-mp="ays1w0960tfz">
              <div className="flex items-center gap-2 text-xs font-medium text-muted" data-id="element-491" data-mp="39gnsln5zo02b">
                <CalendarCheckIcon className="h-4 w-4 text-deep" data-id="element-492" data-mp="39etbyl1r2u41" />
                {isNewHire ? 'First anniversary' : 'Next milestone'}
              </div>
              {isNewHire || !schedule.length ? <>
                  <p className="mt-2 text-lg font-semibold text-navy" data-id="element-493" data-mp="3o6287d05rdq5">{formatDate(employee.hireDate, 'MMM d')}</p>
                  <p className="mt-0.5 text-xs text-muted" data-id="element-494" data-mp="2qp4tvsizz863">{isNewHire ? 'Balance tops up to 15 days' : 'Work anniversary'}</p>
                </> : upcomingTier ? <>
                  <p className="mt-2 text-lg font-semibold text-navy" data-id="element-495" data-mp="2o4oxzrps30h7">{format(milestoneDate(employee.hireDate, upcomingTier), 'MMM d, yyyy')}</p>
                  <p className="mt-0.5 text-xs text-muted" data-id="element-496" data-mp="2j4kjg78fkftm">
                    Steps up to {upcomingTier.days || 'TBD'} days / yr at {upcomingTier.afterYears} years
                  </p>
                </> : <>
                  <p className="mt-2 text-lg font-semibold text-navy" data-id="element-497" data-mp="25746ecvmpnsw">Top tier reached</p>
                  <p className="mt-0.5 text-xs text-muted" data-id="element-498" data-mp="20fm6zt997va1">Highest accrual on the schedule</p>
                </>}
            </div>
          </div>
        </div> : <Card title="Time off balance" icon={SunIcon} data-id="element-499" data-mp="1qi82ms0luwy3">
          <EmptyState icon={SunIcon} title="No time off policy assigned" description="Regular PTO currently covers the Operations pilot group, and new hires are enrolled in New Hire PTO." action={<Button size="sm" onClick={() => onNavigate({
        page: 'timeoff',
        tab: 'policies'
      })} data-id="element-501" data-mp="35e95xk2hizzf">
                View policies
              </Button>} data-id="element-500" data-mp="1qjj42l6kvsm1" />
        </Card>}

      <Card title="Upcoming time off" icon={PlaneIcon} subtitle={upcoming.length ? `${upcoming.length} scheduled` : undefined} bodyClassName="px-3 pb-3" data-id="element-502" data-mp="2vg1ypsxzzwam">
        {upcoming.length === 0 ? <div className="px-3 pb-3" data-id="element-503" data-mp="2mnhumcyxszie">
            <EmptyState icon={PlaneIcon} title="Nothing scheduled" compact data-id="element-504" data-mp="2mmgis6a3k8zm" />
          </div> : <ul className="space-y-0.5" data-id="element-505" data-mp="2rezxx7ofnc6j">
            {upcoming.map(u => <li key={u.id} className="flex items-center gap-4 rounded-xl px-3 py-3 hover:bg-canvas/70" data-id="element-506" data-mp="2cq3honc82suu">
                <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-navy/[0.05] text-navy" data-id="element-507" data-mp="2hpgf220pzibo">
                  <span className="text-[10px] font-semibold uppercase leading-none" data-id="element-508" data-mp="23pfdekcms6v5">{formatDate(u.start, 'MMM')}</span>
                  <span className="text-lg font-semibold leading-tight" data-id="element-509" data-mp="27s1xu06z2pib">{formatDate(u.start, 'd')}</span>
                </div>
                <div className="min-w-0 flex-1" data-id="element-510" data-mp="1k3dih9dfzeak">
                  <p className="text-sm font-medium text-navy" data-id="element-511" data-mp="1k4on1bepoq2q">{formatRange(u.start, u.end)}</p>
                  <p className="text-xs text-muted" data-id="element-512" data-mp="1y3woyc29ecam">
                    {u.label} · {formatDays(u.days)}
                  </p>
                </div>
                <Badge tone={u.pending ? 'orange' : 'green'} dot data-id="element-513" data-mp="2809h35i5oi39">
                  {u.pending ? 'Pending' : 'Approved'}
                </Badge>
              </li>)}
          </ul>}
      </Card>

      <Card title="History" icon={HistoryIcon} bodyClassName="pb-2" action={history.length > 0 && <div className="flex gap-2" data-id="element-515" data-mp="2ctm4jcb3miuf">
              <FilterSelect label="Policy" value={policyFilter} options={[policy?.name ?? 'All']} onChange={setPolicyFilter} className="w-40" data-id="element-516" data-mp="2qqr7v4jfh3lz" />
              <FilterSelect label="Year" value={year} options={years} onChange={setYear} className="w-24" data-id="element-517" data-mp="2lu0rsh7mi80k" />
            </div>} data-id="element-514" data-mp="2gtcs775bwiuq">
        {isRegular && currentTier && <p className="mx-6 mb-3 rounded-xl bg-azure/[0.06] px-4 py-2.5 text-xs text-slate-600 ring-1 ring-inset ring-azure/15" data-id="element-518" data-mp="29ho5mxhy1538">
            Regular PTO grants the yearly amount for the employee's tier on {tierPosting(currentTier).replace(' each year', '')}; up to {currentTier.carryover || 0} unused days carry over at year end.
          </p>}
        {rows.length === 0 ? <div className="px-6 pb-4" data-id="element-519" data-mp="2t8jf3yw6j4y5">
            <EmptyState icon={HistoryIcon} title="No history yet" compact data-id="element-520" data-mp="2t5dp8hfq7z1d" />
          </div> : <div className="overflow-x-auto" data-id="element-521" data-mp="2o9y9tl22lv2u">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-522" data-mp="2j7z9kk1mnea4">
              <thead data-id="element-523" data-mp="1csoxdihd03b1">
                <tr className="border-y border-slate-100 bg-canvas/70 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-524" data-mp="1ctzxn8blisxe">
                  <th scope="col" className="px-6 py-2.5" data-id="element-525" data-mp="17skclg9f4mf0">Date</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-526" data-mp="xqzuob7sdtzn">Description</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-527" data-mp="12oi7icoxctcx">Used (-)</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-528" data-mp="17o4kcl0uc7l7">Earned (+)</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-529" data-mp="17m1npi56ng7i">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-530" data-mp="1ggon9xo60ybu">
                {rows.map(h => <tr key={h.id} className="transition-colors hover:bg-azure/[0.03]" data-id="element-531" data-mp="1lf9cl21vpxli">
                    <td className="whitespace-nowrap px-6 py-3 text-slate-600" data-id="element-532" data-mp="1lhc9m61g8qzq">{formatDate(h.date, 'MM/dd/yyyy')}</td>
                    <td className="px-6 py-3 text-slate-800" data-id="element-533" data-mp="1vdfcxf81evo9">{h.description}</td>
                    <td className="px-6 py-3 text-right tabular-nums text-rose-600" data-id="element-534" data-mp="1vbcmwv6vx2jj">{h.used ? `-${h.used.toFixed(2)}` : ''}</td>
                    <td className="px-6 py-3 text-right tabular-nums text-emerald-600" data-id="element-535" data-mp="z653djhjni9">
                      {h.pendingAccrual ? <TbdBadge data-id="element-536" data-mp="3p3kvyik99594" /> : h.earned ? `+${h.earned.toFixed(2)}` : ''}
                    </td>
                    <td className="px-6 py-3 text-right font-semibold tabular-nums text-navy" data-id="element-537" data-mp="3k6clz599g7iy">{h.balance.toFixed(2)}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>
    </div>;
}